import type { FileUpload } from 'graphql-upload/GraphQLUpload.js'
import GraphQLUpload from 'graphql-upload/GraphQLUpload.js'
import { ObjectType, Field, InputType, Int, ID, registerEnumType } from 'type-graphql'

import { ObjectRef, ScalarObject } from '@things-factory/shell'

import { Kpi, KpiStatus, KpiVizType, KpiScoreType, KpiValueType } from './kpi'
import { KpiScores } from './kpi-grade.types'

@InputType({ description: 'Input type for creating a new KPI. Used in mutations to provide KPI details.' })
export class NewKpi {
  @Field({ description: 'Name of the KPI.' })
  name: string

  @Field({ nullable: true, description: 'Detailed description of the KPI.' })
  description?: string

  @Field(type => ObjectRef, { nullable: true, description: 'Reference to the parent KPI in hierarchical structure.' })
  parent?: ObjectRef

  @Field({ nullable: true, description: 'Indicates whether this KPI is a leaf node (has actual measured values).' })
  isLeaf?: boolean

  @Field({ nullable: true, description: 'Calculation formula for the KPI, using metric codes and operators.' })
  formula?: string

  @Field({ nullable: true, description: 'Indicates whether this KPI is active and usable.' })
  active?: boolean

  @Field(type => KpiStatus, { nullable: true, description: 'Current state of the KPI (DRAFT, RELEASED, ARCHIVED).' })
  state?: KpiStatus

  @Field(type => GraphQLUpload, { nullable: true, description: 'Thumbnail image or file for this KPI.' })
  thumbnail?: FileUpload

  @Field(type => KpiVizType, {
    nullable: true,
    description: 'Visualization type for this KPI (e.g., CARD, GAUGE, PROGRESS, BAR, LINE, etc.).'
  })
  vizType?: KpiVizType

  @Field(type => ScalarObject, {
    nullable: true,
    description:
      'Visualization options and metadata for this KPI, such as color, icon, thresholds, unit, decimal places, etc.'
  })
  vizMeta?: any

  @Field({
    nullable: true,
    description: 'Cron schedule string for periodic KPI value aggregation (e.g., "0 0 * * *" for daily).'
  })
  schedule?: string

  @Field({ nullable: true, description: 'Timezone for the KPI schedule.' })
  timezone?: string

  @Field({ nullable: true, description: 'Weight for aggregation in parent category.' })
  weight?: number

  @Field(type => KpiScoreType, {
    nullable: true,
    description: 'value → score 변환 방식.'
  })
  scoreType?: KpiScoreType

  @Field(type => KpiValueType, {
    nullable: true,
    description: 'value 획득 방식.'
  })
  valueType?: KpiValueType

  @Field(type => ScalarObject, {
    nullable: true,
    description: 'Score lookup table for this KPI version'
  })
  grades?: KpiScores

  @Field({
    nullable: true,
    description: 'Score calculation formula for this KPI. Converts KPI value to performance score (0-1).'
  })
  scoreFormula?: string
}

@InputType({ description: 'Input type for updating an existing KPI. Used in mutations to patch KPI details.' })
export class KpiPatch {
  @Field(type => ID, { nullable: true, description: 'ID of the KPI to update.' })
  id?: string

  @Field({ nullable: true, description: 'Name of the KPI.' })
  name?: string

  @Field({ nullable: true, description: 'Detailed description of the KPI.' })
  description?: string

  @Field(type => ObjectRef, { nullable: true, description: 'Reference to the parent KPI in hierarchical structure.' })
  parent?: ObjectRef

  @Field({ nullable: true, description: 'Indicates whether this KPI is a leaf node (has actual measured values).' })
  isLeaf?: boolean

  @Field({ nullable: true, description: 'Calculation formula for the KPI, using metric codes and operators.' })
  formula?: string

  @Field({ nullable: true, description: 'Indicates whether this KPI is active and usable.' })
  active?: boolean

  @Field(type => KpiStatus, { nullable: true, description: 'Current state of the KPI (DRAFT, RELEASED, ARCHIVED).' })
  state?: KpiStatus

  @Field(type => GraphQLUpload, { nullable: true, description: 'Thumbnail image or file for this KPI.' })
  thumbnail?: FileUpload

  @Field(type => KpiVizType, {
    nullable: true,
    description: 'Visualization type for this KPI (e.g., CARD, GAUGE, PROGRESS, BAR, LINE, etc.).'
  })
  vizType?: KpiVizType

  @Field(type => ScalarObject, {
    nullable: true,
    description:
      'Visualization options and metadata for this KPI, such as color, icon, thresholds, unit, decimal places, etc.'
  })
  vizMeta?: any

  @Field({ nullable: true, description: 'Custom flag for update operations (internal use).' })
  cuFlag?: string

  @Field({
    nullable: true,
    description: 'Cron schedule string for periodic KPI value aggregation (e.g., "0 0 * * *" for daily).'
  })
  schedule?: string

  @Field({ nullable: true, description: 'Timezone for the KPI schedule.' })
  timezone?: string

  @Field({ nullable: true, description: 'Weight for aggregation in parent category.' })
  weight?: number

  @Field(type => KpiScoreType, {
    nullable: true,
    description: 'value → score 변환 방식.'
  })
  scoreType?: KpiScoreType

  @Field(type => KpiValueType, {
    nullable: true,
    description: 'value 획득 방식.'
  })
  valueType?: KpiValueType

  @Field(type => ScalarObject, {
    nullable: true,
    description: 'Score lookup table for this KPI version'
  })
  grades?: KpiScores

  @Field({
    nullable: true,
    description: 'Score calculation formula for this KPI. Converts KPI value to performance score (0-1).'
  })
  scoreFormula?: string
}

@ObjectType()
export class KpiList {
  @Field(type => [Kpi])
  items: Kpi[]

  @Field(type => Int)
  total: number
}
